import { Actor } from '@icp-sdk/core/agent';
import { IDL } from '@icp-sdk/core/candid';
import { publicAgent, type Candle } from './api';

export const SWAP_API = 'https://api.icpswap.com/info';
export interface PoolSummary {
  poolId: string; token0LedgerId: string; token1LedgerId: string;
  token0Price: string | null; token1Price: string | null;
  tvlUSD: string | null; volumeUSD24H: string | null; volumeUSD7D?: string | null; totalVolumeUSD: string | null; txCount24H: string | null;
}
export interface PoolPrice { basePerToken: number; at: number }
interface Metadata { token0: { address: string }; token1: { address: string }; sqrtPriceX96: bigint }
const poolIdl: IDL.InterfaceFactory = ({ IDL: T }) => T.Service({ metadata: T.Func([], [T.Variant({
  ok: T.Record({ token0: T.Record({ address: T.Text }), token1: T.Record({ address: T.Text }), sqrtPriceX96: T.Nat }),
  err: T.Variant({ CommonError: T.Null, InsufficientFunds: T.Null, InternalError: T.Text, UnsupportedToken: T.Text }),
})], ['query']) });

export function finite(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const result = Number(value);
  return Number.isFinite(result) && result >= 0 ? result : null;
}
export function poolRatio(meta: Metadata, ledger: string, base: string, baseDecimals: number): number {
  const zero = meta.token0.address === ledger && meta.token1.address === base;
  if (!zero && !(meta.token1.address === ledger && meta.token0.address === base)) throw Error('ICPSwap pool does not match this token and base ledger.');
  const sqrt = Number(meta.sqrtPriceX96) / 2 ** 96, ratio = sqrt * sqrt;
  const price = (zero ? ratio : 1 / ratio) * 10 ** (8 - baseDecimals);
  if (!Number.isFinite(price) || price <= 0) throw Error('ICPSwap returned an invalid price.');
  return price;
}
export async function poolPrice(pool: string, ledger: string, base: string, baseDecimals: number): Promise<PoolPrice> {
  const actor = Actor.createActor<{ metadata: () => Promise<{ ok: Metadata } | { err: unknown }> }>(poolIdl, { agent: await publicAgent(), canisterId: pool });
  const result = await actor.metadata();
  if ('err' in result) throw Error('ICPSwap pool metadata is unavailable.');
  return { basePerToken: poolRatio(result.ok, ledger, base, baseDecimals), at: Date.now() };
}
async function read<T>(path: string): Promise<T> {
  const response = await fetch(`${SWAP_API}/${path}`, { signal: AbortSignal.timeout(20_000) });
  if (!response.ok) throw Error(`ICPSwap analytics is unavailable (${response.status}).`);
  const result = await response.json();
  if (result.code !== 200 || result.data === null || result.data === undefined) throw Error('ICPSwap analytics returned no data.');
  return result.data as T;
}
let summaries: { expires: number; promise: Promise<Map<string, PoolSummary>> } | undefined;
export function poolSummaries(): Promise<Map<string, PoolSummary>> {
  if (summaries && summaries.expires > Date.now()) return summaries.promise;
  const promise = read<PoolSummary[]>('pool/all').then(rows => {
    if (!Array.isArray(rows)) throw Error('Invalid ICPSwap pool list.');
    return new Map(rows.map(row => [row.poolId, row]));
  }).catch(error => { summaries = undefined; throw error; });
  summaries = { expires: Date.now() + 30_000, promise };
  return promise;
}
export function matchingSummary(row: PoolSummary | undefined, ledger: string, base: string) {
  return row && ((row.token0LedgerId === ledger && row.token1LedgerId === base) || (row.token1LedgerId === ledger && row.token0LedgerId === base)) ? row : undefined;
}
export function decimalUnits(value: string, decimals: number): bigint {
  if (!/^\d+(\.\d+)?$/.test(value)) throw Error('Invalid token amount from ICPSwap.');
  const [whole, fraction = ''] = value.split('.');
  return BigInt(whole) * 10n ** BigInt(decimals) + BigInt((fraction + '0'.repeat(decimals)).slice(0, decimals) || '0');
}
export interface Swap {
  poolId: string; actionType: string; token0LedgerId: string; token1LedgerId: string;
  token0AmountIn: string; token1AmountIn: string; token0AmountOut: string; token1AmountOut: string;
  token0TxValue: string; token1TxValue: string; token0Price: string; token1Price: string;
  fromPrincipalId: string; txTime: number; txHash: string;
}
export interface SwapPage { content: Swap[]; totalElements: number; page: number; limit: number }
export async function poolTrades(pool: string, owner?: string, page = 1, limit = 200): Promise<SwapPage> {
  const params = new URLSearchParams({ poolId: pool, actionTypes: 'Swap', page: String(page), limit: String(limit) });
  if (owner) params.set('principal', owner);
  return read<SwapPage>(`transaction/find?${params}`);
}
export function swapAmounts(trade: Swap, ledger: string, base: string) {
  const zero = trade.token0LedgerId === ledger && trade.token1LedgerId === base;
  if (!zero && !(trade.token1LedgerId === ledger && trade.token0LedgerId === base)) throw Error('Trade ledgers do not match this pair.');
  const tokenIn = zero ? trade.token0AmountIn : trade.token1AmountIn;
  const tokenOut = zero ? trade.token0AmountOut : trade.token1AmountOut;
  const baseIn = zero ? trade.token1AmountIn : trade.token0AmountIn;
  const baseOut = zero ? trade.token1AmountOut : trade.token0AmountOut;
  if ((decimalUnits(tokenIn, 8) > 0n) === (decimalUnits(tokenOut, 8) > 0n)) throw Error('Swap token flow is invalid.');
  const buy = decimalUnits(tokenOut, 8) > 0n;
  const tokens = decimalUnits(buy ? tokenOut : tokenIn, 8);
  const baseAmount = buy ? baseIn : baseOut;
  if (!(Number(baseAmount) > 0)) throw Error('Swap base flow is invalid.');
  const dollars = finite(zero ? trade.token1TxValue : trade.token0TxValue);
  return { buy, tokens, baseAmount, dollars };
}
interface PoolCandle { token0LedgerId: string; token1LedgerId: string; poolId: string; beginTime: number; open: string; high: string; low: string; close: string; volumeToken0: string; volumeToken1: string; txCount: string }
export async function poolCandles(pool: string, ledger: string, base: string, baseDecimals: number, hourly: boolean): Promise<Candle[]> {
  // Pool OHLC is token1 per token0, already adjusted for decimals. USD conversion happens in the chart.
  const page = await read<{ content: PoolCandle[] }>(`pool/${pool}/chart/${hourly ? 'h1' : 'm1'}?page=1&limit=500`);
  return page.content.map(row => {
    if (!matchingSummary(row as unknown as PoolSummary, ledger, base) || row.poolId !== pool) throw Error('Candle ledgers do not match this pair.');
    const zero = row.token0LedgerId === ledger;
    const [o, h, l, c] = [row.open, row.high, row.low, row.close].map(Number);
    if (![o, h, l, c].every(x => x > 0 && Number.isFinite(x))) return null;
    return { t: BigInt(row.beginTime) * 1_000_000n, open: zero ? o : 1 / o, high: zero ? h : 1 / l, low: zero ? l : 1 / h, close: zero ? c : 1 / c, volume: decimalUnits(zero ? row.volumeToken1 : row.volumeToken0, baseDecimals) };
  }).filter((row): row is Candle => row !== null);
}
